/**
 * @fileOverview The hover look of what the demo pointer is on, in the frame. Pure DOM.
 * See docs/view-help.md, "The pointer".
 *
 * The pointer is a drawing, and a dispatched `mouseover` does not give an element `:hover` — only the
 * browser's own mouse does. So a link the pointer was about to press never underlined, a button never
 * lit up, and a click on a line of text that navigates looked like a click on nothing in particular.
 *
 * Every `:hover` rule of the frame's stylesheets is therefore copied once, with `:hover` replaced by an
 * attribute, into a sheet of our own; the element under the pointer and its ancestors get the
 * attribute, as the browser would give them `:hover`. The copies keep their `@media`, `@supports` and
 * `@layer` around them, so they win and lose exactly where the originals do. Sheets the app adds later
 * — a lazily loaded view's styles — are picked up on the next mark.
 */

const ATTRIBUTE = "data-vhelp-hover"
const SHEET_ID = "vhelp-hover-rules"

/** Sheets already copied, per sheet object — a reloaded frame brings new ones. */
const copied = new WeakSet<CSSStyleSheet>()

/** The rule, with every `:hover` in its selector turned into the attribute, or null without one. */
function mirroredSelector(selector: string): string | null {
  return selector.includes(":hover") ? selector.replace(/:hover\b/g, `[${ATTRIBUTE}]`) : null
}

/**
 * Copies the `:hover` rules of `rules` into `into`, inside the at-rules they were found in. Rules are
 * told apart by their constructor's name, not `instanceof`: they belong to the frame's window, whose
 * `CSSStyleRule` is not the host's.
 */
function copyRules(rules: CSSRuleList, wrappers: string[], into: CSSStyleSheet) {
  for (const rule of Array.from(rules)) {
    const kind = rule.constructor.name
    if (kind === "CSSStyleRule") {
      const style = rule as CSSStyleRule
      const selector = mirroredSelector(style.selectorText)
      if (!selector) continue
      const text = wrappers.reduceRight(
        (inner, wrapper) => `${wrapper} { ${inner} }`,
        `${selector} { ${style.style.cssText} }`
      )
      try {
        into.insertRule(text, into.cssRules.length)
      } catch {
        // A selector this browser parses in the original but not rewritten — rare, and only a look.
      }
    } else if (kind === "CSSMediaRule" || kind === "CSSSupportsRule") {
      const group = rule as CSSMediaRule | CSSSupportsRule
      const at = kind === "CSSMediaRule" ? "@media" : "@supports"
      copyRules(group.cssRules, [...wrappers, `${at} ${group.conditionText}`], into)
    } else if (kind === "CSSLayerBlockRule") {
      const layer = rule as CSSLayerBlockRule
      copyRules(layer.cssRules, [...wrappers, `@layer ${layer.name}`], into)
    }
  }
}

/** Our sheet in `doc`, created on first use. */
function ownSheet(doc: Document): CSSStyleSheet | null {
  let style = doc.getElementById(SHEET_ID) as HTMLStyleElement | null
  if (!style) {
    style = doc.createElement("style")
    style.id = SHEET_ID
    doc.head.appendChild(style)
  }
  return style.sheet
}

/** Copies the hover rules of every sheet not copied yet. */
function mirrorHoverRules(doc: Document) {
  const into = ownSheet(doc)
  if (!into) return
  for (const sheet of Array.from(doc.styleSheets)) {
    if (sheet === into || copied.has(sheet)) continue
    let rules: CSSRuleList
    try {
      rules = sheet.cssRules
    } catch {
      // A cross-origin sheet (a web font's) cannot be read, and has no hover rules worth having.
      copied.add(sheet)
      continue
    }
    copied.add(sheet)
    copyRules(rules, [], into)
  }
}

/** Takes the hover look off everything in `doc`. */
export function clearHover(doc: Document | null | undefined) {
  if (!doc) return
  for (const el of Array.from(doc.querySelectorAll(`[${ATTRIBUTE}]`))) el.removeAttribute(ATTRIBUTE)
}

/** Gives `el` and its ancestors the hover look, and takes it off whatever had it before. */
export function hoverOver(el: Element) {
  const doc = el.ownerDocument
  mirrorHoverRules(doc)
  const chain = new Set<Element>()
  for (let at: Element | null = el; at; at = at.parentElement) chain.add(at)
  for (const marked of Array.from(doc.querySelectorAll(`[${ATTRIBUTE}]`))) {
    if (!chain.has(marked)) marked.removeAttribute(ATTRIBUTE)
  }
  for (const at of chain) at.setAttribute(ATTRIBUTE, "")
}

/** Whether pressing `el` follows a link — a line of text that navigates, which deserves a longer look. */
export function isLink(el: Element): boolean {
  return el.closest("a[href]") !== null
}
